/**
 * MathML Sanitization Utility
 *
 * Validates and auto-fixes common MathML issues in PIE content to prevent rendering crashes.
 * Handles:
 * - xmlns attribute format issues (angle brackets around URLs)
 * - CDATA sections that break HTML parsers
 */

/**
 * Security limits for ReDoS prevention
 */
const SANITIZER_LIMITS = {
  /** Maximum input string length before skipping sanitization */
  MAX_INPUT_LENGTH: 100000,
  /** Maximum CDATA section content length for regex matching */
  MAX_CDATA_LENGTH: 10000,
} as const

/**
 * Base interface for validation warnings
 */
interface BaseValidationWarning {
  field: string // JSON path like "models[0].prompt"
  originalValue: string
  fixedValue: string
}

/**
 * Warning for xmlns attribute fixes
 */
export interface XmlnsValidationWarning extends BaseValidationWarning {
  type: 'xmlns'
  message: 'Fixed malformed xmlns attribute'
}

/**
 * Warning for CDATA section removals
 */
export interface CdataValidationWarning extends BaseValidationWarning {
  type: 'cdata'
  message: 'Removed CDATA section to prevent parser issues'
}

/**
 * Discriminated union of all validation warning types
 */
export type ValidationWarning = XmlnsValidationWarning | CdataValidationWarning

export interface SanitizationResult {
  config: any
  warnings: ValidationWarning[]
  hasIssues: boolean
}

interface FixResult {
  value: string
  fixed: boolean
  message?: string
}

interface SanitizeFieldResult {
  value: string
  warnings: ValidationWarning[]
  modified: boolean
}

interface SanitizeChoicesResult {
  updatedChoices: any
  choicesModified: boolean
  choicesWarnings: ValidationWarning[]
}

/**
 * Detects if a string contains MathML content
 */
export function containsMathML(content: string): boolean {
  if (!content || typeof content !== 'string') return false

  // Look for <math or <mn or <mo tags (case-insensitive)
  return /<math[\s>]/i.test(content) || /<m[no][\s>]/i.test(content) || /xmlns.*math/i.test(content)
}

/**
 * Fixes malformed xmlns attributes with angle brackets around URLs
 * e.g., xmlns="<http://www.w3.org/1998/Math/MathML>" -> xmlns="http://www.w3.org/1998/Math/MathML"
 */
export function fixXmlnsAttribute(mathmlString: string): FixResult {
  let fixed = false
  let value = mathmlString

  // Fix angle brackets around URLs
  const xmlnsPattern = /(xmlns\s*=\s*['"])<?([^'"<>]+)>?(['"])/gi
  const valueWithBracketsFix = value.replace(xmlnsPattern, (match, prefix, url, suffix) => {
    // Check if URL has angle brackets
    if (match.includes('<') && match.includes('>')) {
      if (!fixed) fixed = true
      // Remove angle brackets from URL
      return `${prefix}${url.replace(/^<+|>+$/g, '')}${suffix}`
    }
    return match
  })

  if (valueWithBracketsFix !== value) {
    fixed = true
    value = valueWithBracketsFix
  }

  return {
    value,
    fixed,
    message: fixed ? 'Fixed malformed xmlns attribute' : undefined,
  }
}

/**
 * Removes or fixes CDATA sections that may cause HTML parser issues
 * CDATA sections are XML-specific and can break HTML parsers like Slate.js
 *
 * **Security:**
 * - Inputs > 100KB are skipped to prevent memory exhaustion
 * - CDATA sections > 10KB are not matched to prevent ReDoS attacks
 * - Bounded quantifiers prevent catastrophic backtracking
 *
 * Matches any HTML element (span, div, p, etc.) with a class containing "MathML"
 * that has a CDATA section as a direct child.
 *
 * Examples matched:
 * - <span class="MathML"><![CDATA[...]]></span>
 * - <div class='MathML ib-math'><![CDATA[...]]></div>
 * - <p class="foo MathML bar"><![CDATA[...]]></p>
 *
 * Examples NOT matched (by design):
 * - <span class="other"><![CDATA[...]]></span>  (no MathML class)
 * - <span class="MathML"><strong><![CDATA[...]]></strong></span>  (nested CDATA)
 * - <span class="MathML"><![CDATA[...]]></div>  (mismatched tags)
 */
export function fixCdataSection(htmlString: string): FixResult {
  let fixed = false
  let value = htmlString

  // Skip sanitization for extremely large inputs to prevent memory exhaustion
  if (htmlString.length > SANITIZER_LIMITS.MAX_INPUT_LENGTH) {
    console.warn('MathML Sanitizer: Input too large for CDATA processing', {
      length: htmlString.length,
      limit: SANITIZER_LIMITS.MAX_INPUT_LENGTH,
    })
    return { value: htmlString, fixed: false, message: undefined }
  }

  // Check if there are CDATA sections (with bounded quantifier to prevent ReDoS)
  const cdataPattern = new RegExp(`<!\\[CDATA\\[[\\s\\S]{0,${SANITIZER_LIMITS.MAX_CDATA_LENGTH}}?\\]\\]>`, 'gi')
  if (cdataPattern.test(htmlString)) {
    // Remove CDATA sections entirely since they may break the HTML parser
    // The MathML content should already exist in the actual <math> element above
    // Remove CDATA sections from any HTML element with MathML class
    // Uses backreference (\1) to ensure opening and closing tags match
    // Handles both single and double quotes in class attributes
    value = htmlString.replace(
      new RegExp(
        `<(\\w+)[^>]*class=["'][^"']*MathML[^"']*["'][^>]*>\\s*<!\\[CDATA\\[[\\s\\S]{0,${SANITIZER_LIMITS.MAX_CDATA_LENGTH}}?\\]\\]>\\s*<\\/\\1>`,
        'gi'
      ),
      ''
    )

    if (value !== htmlString) {
      fixed = true
    }
  }

  return {
    value,
    fixed,
    message: fixed ? 'Removed CDATA section to prevent parser issues' : undefined,
  }
}

/**
 * Sanitizes a single field containing MathML
 * Applies xmlns and CDATA fixes, tracks warnings
 */
export function sanitizeField(value: string, fieldPath: string): SanitizeFieldResult {
  const warnings: ValidationWarning[] = []
  let currentValue = value
  let modified = false

  // Check if value contains MathML
  if (!containsMathML(value)) {
    return { value, warnings, modified }
  }

  // Apply xmlns fixes
  const xmlnsResult = fixXmlnsAttribute(currentValue)
  if (xmlnsResult.fixed) {
    warnings.push({
      type: 'xmlns',
      field: fieldPath,
      originalValue: value,
      fixedValue: xmlnsResult.value,
      message: 'Fixed malformed xmlns attribute',
    })
    currentValue = xmlnsResult.value
    modified = true
  }

  // Apply CDATA section fixes
  const cdataResult = fixCdataSection(currentValue)
  if (cdataResult.fixed) {
    warnings.push({
      type: 'cdata',
      field: fieldPath,
      originalValue: currentValue,
      fixedValue: cdataResult.value,
      message: 'Removed CDATA section to prevent parser issues',
    })
    currentValue = cdataResult.value
    modified = true
  }

  return { value: currentValue, warnings, modified }
}

/**
 * Sanitizes choices[].label fields in a model
 * Handles two structures:
 * 1. Nested object: { "0": [...], "1": [...], etc } (explicit-constructed-response)
 * 2. Direct array: [{...}, {...}, ...] (multiple-choice)
 */
export function sanitizeChoices(choices: any, modelIndex: number): SanitizeChoicesResult {
  const choicesWarnings: ValidationWarning[] = []
  let choicesModified = false

  // Handle direct array structure (multiple-choice)
  if (Array.isArray(choices)) {
    const updatedChoices = choices.map((choice: any, choiceIndex: number) => {
      if (!choice?.label || typeof choice.label !== 'string') {
        return choice
      }

      const fieldPath = `models[${modelIndex}].choices[${choiceIndex}].label`
      const result = sanitizeField(choice.label, fieldPath)

      if (result.modified) {
        choicesWarnings.push(...result.warnings)
        choicesModified = true
        return { ...choice, label: result.value }
      }

      return choice
    })

    return { updatedChoices, choicesModified, choicesWarnings }
  }

  // Handle nested object structure (explicit-constructed-response)
  const updatedChoices: any = {}

  // Iterate through choice keys (e.g., "0", "1", "2")
  for (const choiceKey of Object.keys(choices)) {
    const choiceArray = choices[choiceKey]

    // Skip if not an array
    if (!Array.isArray(choiceArray)) {
      updatedChoices[choiceKey] = choiceArray
      continue
    }

    // Track if this specific array was modified
    let arrayModified = false

    // Sanitize each choice's label
    const updatedChoiceArray = choiceArray.map((choice: any, choiceIndex: number) => {
      if (!choice?.label || typeof choice.label !== 'string') {
        return choice
      }

      const fieldPath = `models[${modelIndex}].choices[${choiceKey}][${choiceIndex}].label`
      const result = sanitizeField(choice.label, fieldPath)

      if (result.modified) {
        choicesWarnings.push(...result.warnings)
        choicesModified = true
        arrayModified = true
        return { ...choice, label: result.value }
      }

      return choice
    })

    updatedChoices[choiceKey] = arrayModified ? updatedChoiceArray : choiceArray
  }

  return { updatedChoices, choicesModified, choicesWarnings }
}

/**
 * Main sanitization function for PIE configs
 * Checks config.models[].prompt, config.models[].markup, and config.models[].choices[].label properties for MathML issues
 */
export function sanitizePieConfig(config: any): SanitizationResult {
  if (!config?.models || !Array.isArray(config.models)) {
    return { config, warnings: [], hasIssues: false }
  }

  const warnings: ValidationWarning[] = []
  let configModified = false

  // Check each model's prompt, markup, and choices properties for MathML
  const updatedModels = config.models.map((model: any, modelIndex: number) => {
    let modelModified = false
    let updatedModel = model

    // Sanitize prompt (existing field)
    if (model?.prompt && typeof model.prompt === 'string') {
      const result = sanitizeField(model.prompt, `models[${modelIndex}].prompt`)
      if (result.modified) {
        warnings.push(...result.warnings)
        updatedModel = { ...updatedModel, prompt: result.value }
        modelModified = true
      }
    }

    // Sanitize markup (new field)
    if (model?.markup && typeof model.markup === 'string') {
      const result = sanitizeField(model.markup, `models[${modelIndex}].markup`)
      if (result.modified) {
        warnings.push(...result.warnings)
        updatedModel = { ...updatedModel, markup: result.value }
        modelModified = true
      }
    }

    // Sanitize choices[].label (supports both array and object structures)
    if (model?.choices && (Array.isArray(model.choices) || typeof model.choices === 'object')) {
      const { updatedChoices, choicesModified, choicesWarnings } = sanitizeChoices(model.choices, modelIndex)
      if (choicesModified) {
        warnings.push(...choicesWarnings)
        modelModified = true
      }
      // Always preserve choices in the updated model, even if not modified
      updatedModel = { ...updatedModel, choices: updatedChoices }
    }

    if (modelModified) {
      configModified = true
      return updatedModel
    }

    return model
  })

  // Only create new config if modifications were made
  const resultConfig = configModified ? { ...config, models: updatedModels } : config

  return {
    config: resultConfig,
    warnings,
    hasIssues: warnings.length > 0,
  }
}
